import axios from 'axios';

export const API_URL = `${process.env.REACT_APP_API_BASE}/api`;


//

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.status, error.message);
    
    // Handle specific error cases
    if (error.response?.status === 404) {
      console.error('Resource not found');
    } else if (error.response?.status === 500) {
      console.error('Server error');
    } else if (error.code === 'ECONNABORTED') {
      console.error('Request timeout');
    }
    
    return Promise.reject(error);
  }
);

// Guidebook API
export const guidebookAPI = {
  getBySlug: async (slug) => {
    try {
      const response = await api.get(`/guidebook/${slug}`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.detail ||
        'Failed to load guidebook. Please check the URL and try again.'
      );
    }
  },

  getCards: async (slug) => {
    try {
      const response = await api.get(`/guidebook/${slug}/cards`);
      return response.data;
    } catch (error) {
      return []; // cards are optional — fail silently
    }
  },
};

// Chat API
export const chatAPI = {
  sendMessage: async (guidebookSlug, message, sessionId = null) => {
    try {
      const response = await api.post('/chat/send', {
        guidebook_slug: guidebookSlug,
        message: message.trim(),
        session_id: sessionId,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.detail ||
        'Failed to send message. Please try again.'
      );
    }
  },

  sendMessageStream: async (guidebookSlug, message, sessionId, onChunk, onDone, onError, location = null) => {
    const response = await fetch(`${API_URL}/chat/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        guidebook_slug: guidebookSlug,
        message: message.trim(),
        session_id: sessionId,
        location: location,
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to send message. Please try again.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop(); // keep incomplete trailing chunk
      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith('data: ')) continue;
        try {
          const data = JSON.parse(line.slice(6));
          if (data.type === 'chunk') onChunk(data.content);
          else if (data.type === 'done') onDone(data);
          else if (data.type === 'error') onError(data.message);
        } catch (_) { /* ignore malformed lines */ }
      }
    }
  },
  
  submitContact: async (sessionId, question, phone, email) => {
    try {
      const response = await api.post('/chat/contact', {
        session_id: sessionId,
        question,
        phone: phone || null,
        email: email || null,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.detail || 
        'Failed to save contact information. Please try again.'
      );
    }
  },
};

// Session API
export const sessionAPI = {
  getSession: async (sessionId) => {
    try {
      const response = await api.get(`/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.detail || 
        'Failed to load session.'
      );
    }
  },
};

export default api;